import { useMemo } from 'react';
import type { HotelDetailsPanelProps } from '@voll-intelligence/intelligence-ui-kit';

import type { GenUiDispatch } from '../../../shared/ui/action.js';
import type { GenUiConfig } from '../../../shared/ui/config.js';
import { genUiT } from '../../../shared/ui/i18n.js';
import {
  type ChatUiMoney,
  buildHotelRoomsBlock,
  rankGroups,
  type ChatUiHotelRoomsRate,
  type HotelRoomsBlock,
  type HotelRoomsRateFilters,
  openingRateFilters,
  type HotelPlannerPort,
  type ChatUiHotelOffer,
  type HotelPlannerPlace,
} from '@voll-intelligence/genui-contract';
import {
  resolveHotelRateDetail,
  resolveHotelRoomSelection,
  type HotelRateSelectionDetail,
} from '../../shared/hotel_rooms_view.js';
import { createHotelDetailsView } from './hotel_details_view.js';
import { staySelection, type HotelStayEcho } from './hotel_selection.js';
import type { HotelReadCache } from './hotel_read_cache.js';
import { useHotelDetails, type HotelDetailsState } from './use_hotel_details.js';

/** Payload de uma tarifa escolhida (gaveta OU página) — o trio que o host
 *  determinístico precisa para tocar pricing→quote por conta própria, sem
 *  mensagem de conversa. */
export type HotelRateSelection = {
  readonly offerId: string;
  readonly roomId: string;
  readonly rateId: string;
  readonly roomIndex: number;
  readonly price?: ChatUiMoney;
  /** O veredito de política que a lista mostrava para a tarifa — o par de
   *  `price` como "o que a pessoa viu": o host compara com o do quote para
   *  avisar quando a tarifa saiu da política no caminho (#3631). */
  readonly policyStatus?: NonNullable<ChatUiHotelRoomsRate['policyStatus']>;
  /** Detalhe exibível da tarifa (quarto/regime) — o checkout determinístico o
   *  espalha no item para o resumo dizer O QUE está sendo pedido. */
  readonly detail?: HotelRateSelectionDetail;
};

/** O coração do detalhe (item 1 F2): estado + callback do HOST — o dono do
 *  estado de favoritos é o mesmo `useHotelFavorites` que alimenta o card, para os
 *  dois corações do mesmo hotel nunca divergirem. Os rótulos são de quem renderiza
 *  (genUiT); a peça do painel é presentational. */
export type HotelDetailsFavoriteControl = {
  readonly pressed: boolean;
  readonly onToggle: () => void;
};

/** O que o detalhe lê do host: o mesmo vocabulário da `GenUiConfig` (o locale,
 *  o proxy das fotos, o token do mapa e a porta do detalhe), por argumento —
 *  a gaveta do chat repassa a config do Context; a bancada, a sua. A porta
 *  precisa de identidade estável: trocar o objeto refaz a leitura do detalhe
 *  (do cache, mas passando por `loading`). */
export type HotelDetailsConfig = Pick<
  GenUiConfig,
  'locale' | 'proxyImageUrl' | 'mapboxAccessToken'
> & {
  readonly ports?: { readonly hotelPlanner?: Pick<HotelPlannerPort, 'getHotelDetails'> };
};

/** A cadeia dados→view do detalhe do hotel, compartilhada pela gaveta
 *  (`HotelDetailsDrawer`) e pela Página do Hotel (`HotelDetailsPage`): busca lazy
 *  do detalhe hotel-level pelo `offerId`, monta o bloco de quartos hidratado,
 *  ranqueia e projeta a `view` que o `HotelDetailsPanel` consome. As duas
 *  variantes só divergem na MOLDURA (gaveta à direita vs página cheia); a fonte
 *  de dados é a mesma. */
export function useHotelDetailsView(
  offer: ChatUiHotelOffer,
  stay: HotelStayEcho,
  cache: HotelReadCache,
  config: HotelDetailsConfig,
  options?: {
    anchor?: HotelPlannerPlace | undefined;
    defer?: boolean;
    /** O recorte de tarifa da lista de onde o hotel veio (#5820). */
    rateFilters?: HotelRoomsRateFilters | undefined;
  },
): {
  view: ReturnType<typeof createHotelDetailsView>;
  rankedRooms: HotelRoomsBlock | null;
  state: HotelDetailsState;
  retry: () => void;
} {
  const { locale, proxyImageUrl, mapboxAccessToken } = config;
  const anchor = options?.anchor;
  const listRateFilters = options?.rateFilters;
  const { state, retry } = useHotelDetails(
    offer.offerId,
    cache,
    config.ports?.hotelPlanner,
    options?.defer ? { defer: true } : undefined,
  );
  const hydratedOffer = useMemo(
    () =>
      state.phase === 'ready'
        ? {
            ...offer,
            rooms: state.data.rooms,
            // Dicionário de ruído (fase 2): repassa os tokens residuais do
            // detalhe ao agrupamento, que os usa para fundir variantes do
            // MESMO quarto entre fornecedores (Task 2/6). Aditivo: sem o
            // campo, o comportamento é o da fase 1.
            ...(state.data.noiseTokens ? { noiseTokens: state.data.noiseTokens } : {}),
          }
        : null,
    [offer, state],
  );
  const roomsBlock = useMemo(
    () => (hydratedOffer ? buildHotelRoomsBlock(hydratedOffer, stay, { locale }) : null),
    [hydratedOffer, stay, locale],
  );
  // Os chips de abertura (#5820) só quando alguma tarifa atende ao recorte
  // inteiro — `openingRateFilters`, a mesma régua do cartão do agente.
  const rankedRooms = useMemo(() => {
    if (!roomsBlock) return null;
    const rateFilters = openingRateFilters(roomsBlock, listRateFilters);
    return {
      ...roomsBlock,
      rooms: rankGroups(roomsBlock.rooms),
      ...(rateFilters ? { rateFilters } : {}),
    };
  }, [roomsBlock, listRateFilters]);
  const view = useMemo(
    () =>
      createHotelDetailsView(offer, state, rankedRooms, {
        locale,
        proxyImageUrl,
        ...(anchor ? { anchor } : {}),
        ...(mapboxAccessToken ? { mapboxAccessToken } : {}),
      }),
    [offer, state, rankedRooms, locale, proxyImageUrl, anchor, mapboxAccessToken],
  );
  return { view, rankedRooms, state, retry };
}

/** Fiação do coração para o `HotelDetailsPanel` (gaveta OU página): mapeia o
 *  controle do host para a prop `favorite` do painel com os rótulos localizados.
 *  Ausente ⇒ `{}` (o painel não mostra o coração). Um só ponto para os dois
 *  renderizadores não divergirem nos rótulos. */
export function hotelFavoritePanelProp(
  favorite: HotelDetailsFavoriteControl | undefined,
  locale: string,
): Pick<HotelDetailsPanelProps, 'favorite'> {
  if (!favorite) return {};
  return {
    favorite: {
      pressed: favorite.pressed,
      markLabel: genUiT(locale, 'hotel.favorite.mark'),
      unmarkLabel: genUiT(locale, 'hotel.favorite.unmark'),
      onToggle: favorite.onToggle,
    },
  };
}

/** Resolve a tarifa escolhida e a entrega ao sink certo. Seam determinístico: com
 *  `onRateSelect` o host toca pricing→quote e NADA vai para a conversa — e a
 *  superfície é DELE: quem avança o estágio (detalhe→checkout) é o próprio
 *  host, então `onClose` NÃO é chamado aqui. (Chamar era redundante no fluxo da
 *  lista — o close guardado virava no-op — e destrutivo na jornada do favorito,
 *  onde o "voltar" desfaz a ENTRADA: Reservar caía na tela de busca.) Sem
 *  `onRateSelect`, o caminho conversacional despacha a ação (card do chat) e
 *  fecha a gaveta. */
export function selectHotelRate(input: {
  readonly rankedRooms: HotelRoomsBlock | null;
  readonly roomId: string;
  readonly rateId: string;
  readonly locale: string;
  readonly stay: HotelStayEcho;
  readonly onClose: () => void;
  readonly onRateSelect?: (selection: HotelRateSelection) => void;
  readonly dispatch?: GenUiDispatch;
}): void {
  const { rankedRooms, roomId, rateId, locale, stay, onClose, onRateSelect, dispatch } = input;
  if (!rankedRooms) return;
  const action = resolveHotelRoomSelection(rankedRooms, roomId, rateId, locale);
  if (!action) return;
  if (onRateSelect) {
    const detail = resolveHotelRateDetail(rankedRooms, roomId, rateId, locale);
    const policyStatus = rankedRooms.rooms
      .flatMap((group) => group.rates)
      .find((rate) => rate.roomIndex === action.roomIndex)?.policyStatus;
    onRateSelect({
      offerId: action.offerId,
      roomId,
      rateId,
      roomIndex: action.roomIndex,
      ...(action.price ? { price: action.price } : {}),
      ...(policyStatus ? { policyStatus } : {}),
      ...(detail ? { detail } : {}),
    });
    return;
  }
  if (!dispatch) return;
  const selectionStay = staySelection(stay);
  dispatch(selectionStay ? { ...action, stay: selectionStay } : action);
  onClose();
}
